import { useEffect } from 'react';
import { useSearchParams, Navigate, Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import VerifiedIcon from '@mui/icons-material/Verified';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { styled, alpha } from '@mui/material/styles';
import { useVerifyEmail } from '../../hooks/auth';
import { Button, LoadingSpinner } from '../../components/ui';
import { AuthLayout } from '../../components/layouts';

const StatusCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  padding: theme.spacing(2),
}));

const SuccessIcon = styled(VerifiedIcon)(({ theme }) => ({
  fontSize: 72,
  color: theme.palette.success.main,
  marginBottom: theme.spacing(2),
  filter: `drop-shadow(0 0 24px ${alpha(theme.palette.success.main, 0.5)})`,
}));

const ErrorIcon = styled(ErrorOutlineIcon)(({ theme }) => ({
  fontSize: 72,
  color: theme.palette.error.main,
  marginBottom: theme.spacing(2),
  filter: `drop-shadow(0 0 24px ${alpha(theme.palette.error.main, 0.5)})`,
}));

export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { mutate: verifyEmail, isPending, isError, isSuccess, error } = useVerifyEmail();

  useEffect(() => {
    if (token) {
      verifyEmail(token);
    }
  }, [token, verifyEmail]);

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  return (
    <AuthLayout title="Email Verification">
      {isPending && (
        <StatusCard>
          <LoadingSpinner size="lg" variant="pulse" />
          <Typography variant="body1" color="text.secondary" sx={{ mt: 3 }}>
            Verifying your email address...
          </Typography>
        </StatusCard>
      )}

      {isSuccess && (
        <StatusCard>
          <SuccessIcon />
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Email Verified!
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Your account is now active. You can now log in to access all features.
          </Typography>
          <Button
            component={Link}
            to="/login"
            fullWidth
            glow
          >
            Continue to Login
          </Button>
        </StatusCard>
      )}

      {isError && (
        <StatusCard>
          <ErrorIcon />
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Verification Failed
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {(error as any)?.response?.data?.message ||
              'Invalid or expired verification link. Please try again or request a new verification email.'}
          </Typography>
          <Button
            component={Link}
            to="/login"
            variant="outline"
            fullWidth
          >
            Back to Login
          </Button>
        </StatusCard>
      )}
    </AuthLayout>
  );
}
